import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation, withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { request } from "../../../../../utils/Request";
import { dateToUtcDate, utcDateToLocalDate } from "../../../../../utils/Helper";
import { Field, Form, Formik, FormikProps } from "formik";
import * as yup from "yup";
import { compact, forEach, get, includes, isArray, isEmpty, uniq, uniqBy } from "lodash";
import moment from "moment";
import { Sidebar } from "primereact/sidebar";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Col, Row } from "react-bootstrap";
import { InputTextAreaField } from "../../../../../components/Shared/InputTextAreaField";
import { Button } from "primereact/button";
import InputDateTime from "../../../../../components/Shared/InputDateTime";
import { InputSingleSelectField } from "../../../../../components/Shared/InputSingleSelectField";
import Location from "../../../../../components/Common/Display/Location";
import Tooltip from "../../../../../components/Common/Display/Tooltip";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";
import GroupedLoadsByRoute from "./GroupedLoadsByRoute";
import VinWithType from "../../../../../components/Common/Display/VinWithType";

interface IFormProps {
    id: string | null; // groupToken
    t: any | null;
    onRefresh?: any | null;
    dropdowns?: any | null;
    onClose?: any | null;
    toastify?: any | null;
}

type TInputForm = {
    driver_id?: any;
    transport_vehicle_id?: any;
    estimated_loading_time?: any;
    estimated_unloading_time?: any;
    comments?: any;
    new_orders?: string[];
    deleted_orders?: string[];
};

const DEFAULT_FORM_STATE: TInputForm = {
    driver_id: null,
    transport_vehicle_id: null,
    estimated_loading_time: null,
    estimated_unloading_time: null,
    comments: null,
    new_orders: [],
    deleted_orders: [],
};

const validationSchema = (t: Function): any => {
    return yup.object().shape({
        driver_id: yup.string().required(t('the field is required.', { field: t('driver') })),
        transport_vehicle_id: yup.string().required(t('the field is required.', { field: t('transport vehicle') })),
        estimated_loading_time: yup.date(t("the field value is not valid", { field: t('estimated loading time') }))
            .required(t('the field is required.', { field: t('estimated loading time') })).nullable(),
        estimated_unloading_time: yup.date(t("the field value is not valid", { field: t('estimated unloading time') }))
            .min(yup.ref('estimated_loading_time'), t('the field must be a date after', {
                field: "estimated unloading time",
                fieldAfter: "estimated loading time"
            }))
            .required(t('the field is required.', { field: t('estimated unloading time') })).nullable(),
        comments: yup.string().nullable(),
    });
};

function AlpOutboundLoadGroupUpdate(props: IFormProps) {
    const { dropdowns, toastify } = props;
    const { t } = useTranslation();
    const formRef: any = useRef();
    const formikRef = useRef<FormikProps<TInputForm> | null>(null);
    const [entity, setEntity] = useState<any>(null);
    const [initFormState, setInitFormState] = useState<any>({ ...DEFAULT_FORM_STATE });
    const [panelState, setPanelState] = useState<boolean>(false);
    const [addOrdersModal, setAddOrdersModal] = useState(false);
    const [addOrdersLoading, setAddOrdersLoading] = useState(false);
    const [availableOrders, setAvailableOrders] = useState<any[]>([]);
    const [selectedAddOrders, setSelectedAddOrders] = useState<any[]>([]);
    const [selectedNewOrders, setSelectedNewOrders] = useState<any[]>([]);
    const [apiErrors, setApiErrors] = useState<any>({});

    const onClose = () => {
        // Always reset local UI state so the next open starts clean (no stale 422 errors / selections).
        setApiErrors({});
        setAddOrdersModal(false);
        setAddOrdersLoading(false);
        setAvailableOrders([]);
        setSelectedAddOrders([]);
        setSelectedNewOrders([]);

        try {
            if ( formRef.current && typeof formRef.current.resetForm === 'function' ) {
                formRef.current.resetForm();
            }
        } catch (e) {
            // ignore
        }

        setPanelState(false);
        if (props.onClose) {
            props.onClose();
        }
    };

    const onSubmit = async (payloads: TInputForm, { setErrors, resetForm }: any) => {
        try {
            setApiErrors({});
            await request({
                method: 'POST',
                url: `/suppliers/alp/aviso/outbound/loads/${ props.id }/update`,
                data: {
                    ...payloads,
                    estimated_unloading_time: (payloads.estimated_unloading_time
                        ? dateToUtcDate(payloads.estimated_unloading_time, 'YYYY-MM-DD[T]HH:mm:ss', 'YYYY-MM-DD HH:mm:ss')
                        : null),
                    estimated_loading_time: (payloads.estimated_loading_time
                        ? dateToUtcDate(payloads.estimated_loading_time, 'YYYY-MM-DD[T]HH:mm:ss', 'YYYY-MM-DD HH:mm:ss')
                        : null),
                },
            });

            toastify && toastify(t("record updated", { item: t('outbound load') }), "info");
            resetForm();
            setAvailableOrders([]);
            setSelectedAddOrders([]);
            setSelectedNewOrders([]);
            onClose();

            if (props.onRefresh) {
                props.onRefresh();
            }
        } catch (e: any) {
            if (e.status === 422) {
                setApiErrors(e.data?.errors ?? {});
                setErrors(e.data?.errors ?? {});
            } else if (e.status === 404) {
                toastify && toastify(t("action not allowed: 404"), "error");
            } else {
                toastify && toastify(t("server error"), "error");
            }
        }
    };

    const getDetail = async (groupToken: string) => {
        try {
            // Ensure previous validation errors never carry over.
            setApiErrors({});

            const response = await request({
                url: `/suppliers/provider/aviso/outbound/groups/${ groupToken }`,
            });
            const data = response?.data?.data ?? response?.data;
            setEntity(data);

            const firstLoad = isArray(data?.alp_outbound_loads) && data.alp_outbound_loads.length > 0
                ? data.alp_outbound_loads[0]
                : null;

            setInitFormState({
                ...DEFAULT_FORM_STATE,
                // Prefill from the first load if available
                driver_id: firstLoad?.driver?.id ?? null,
                transport_vehicle_id: firstLoad?.transport_vehicle?.id ?? null,
                estimated_loading_time: (firstLoad?.estimated_loading_time
                    ? utcDateToLocalDate(firstLoad.estimated_loading_time, 'YYYY-MM-DD[T]HH:mm:ss', 'YYYY-MM-DD HH:mm:ss')
                    : null),
                estimated_unloading_time: (firstLoad?.estimated_unloading_time
                    ? utcDateToLocalDate(firstLoad.estimated_unloading_time, 'YYYY-MM-DD[T]HH:mm:ss', 'YYYY-MM-DD HH:mm:ss')
                    : null),
                comments: null,
                new_orders: [],
                deleted_orders: [],
            });
            setPanelState(true);
        } catch (error: any) {
            if (error.status === 404) {
                toastify && toastify(t("action not allowed: 404"), "error");
            } else {
                toastify && toastify(t("server error"), "error");
            }
        }
    };

    useEffect(() => {
        setEntity(null);
        if (!isEmpty(props.id)) {
            // @ts-ignore
            getDetail(props.id);
        }
    }, [props.id]);

    const loads = isArray(entity?.alp_outbound_loads) ? entity.alp_outbound_loads : [];
    const showAddOrders = false;
    const firstLoad = loads.length > 0 ? loads[0] : null;
    const currentDriver = firstLoad?.driver ?? null;
    const currentTransportVehicle = firstLoad?.transport_vehicle ?? null;

    const driverOptions = useMemo(() => {
        const current = !isEmpty(currentDriver?.id)
            ? [{
                ...currentDriver,
                key: currentDriver.id,
                label: currentDriver.name,
                is_active: true,
            }]
            : [];

        return uniqBy([ ...current, ...(dropdowns?.drivers || []) ], (item: any) => String(item.id));
    }, [dropdowns?.drivers, currentDriver]);

    const transportVehicleOptions = useMemo(() => {
        const current = !isEmpty(currentTransportVehicle?.id)
            ? [{
                ...currentTransportVehicle,
                key: currentTransportVehicle.id,
                label: `${ currentTransportVehicle.truck_plate_number ?? '' } | ${ currentTransportVehicle.trailer_plate_number ?? '' }`,
                is_active: true,
            }]
            : [];

        return uniqBy([ ...current, ...(dropdowns?.transportVehicles || []) ], (item: any) => String(item.id))
            .filter((item: any) => item.is_active != false)
            .map((item: any) => ({
                ...item,
                label: `${ item.truck_plate_number } | ${ item.trailer_plate_number }`,
                icon: (!isEmpty(item.active_journey) ? 'fas fa-circle text-yellow-500' : 'fas fa-circle text-green-500')
            }));
    }, [dropdowns?.transportVehicles, currentTransportVehicle]);

    const canAddOrders = includes(['announced', 'accepted'], (entity?.status || '').toString().toLowerCase());
    const groupProviderIds = useMemo(() => {
        const ids: string[] = [];
        forEach(loads, (load: any) => {
            const providerId = get(load, 'provider_id') || get(load, 'provider.id') || get(load, 'alp_outbound_load_orders.0.order.provider_id');
            if ( !isEmpty(providerId) ) {
                ids.push(String(providerId));
            }
        });

        return uniq(compact(ids));
    }, [loads]);

    const existingOrderIds = useMemo(() => {
        const ids: string[] = [];
        forEach(loads, (load: any) => {
            const loadOrders = get(load, 'alp_outbound_load_orders', []);
            forEach(loadOrders, (lo: any) => {
                const id = get(lo, 'order.id');
                if ( !isEmpty(id) ) {
                    ids.push(String(id));
                }
            });
        });
        return uniq(compact(ids));
    }, [loads]);

    const loadByOrderId = useMemo(() => {
        const map: any = {};
        forEach(loads, (load: any) => {
            const order = get(load, 'alp_outbound_load_orders.0.order');
            const id = String(get(order, 'id') || '');
            if ( !isEmpty(id) ) {
                map[id] = load;
            }
        });
        return map;
    }, [loads]);

    const fetchAvailableOrders = async () => {
        if ( addOrdersLoading ) {
            return;
        }

        setAddOrdersLoading(true);
        try {
            const response = await request({
                url: `/suppliers/orders/transport/requests?1=1`,
                params: {
                    per_page: 0,
                    filters: {
                        providers: groupProviderIds,
                    },
                }
            });

            const rows = response?.data?.data ?? response?.data ?? [];
            const list = isArray(rows) ? rows : [];
            const filtered = list.filter((row: any) => (
                String(get(row, 'source') || '') === 'transport_request'
                && !includes(existingOrderIds, String(get(row, 'id')))
            ));
            setAvailableOrders(filtered);
        } catch (e: any) {
            toastify && toastify(t("server error"), "error");
            setAvailableOrders([]);
        } finally {
            setAddOrdersLoading(false);
        }
    };

    const openAddOrders = async () => {
        if ( !canAddOrders ) {
            toastify && toastify(t("action not allowed: 404"), "error");
            return;
        }

        setAddOrdersModal(true);
        setSelectedAddOrders([]);
        if ( availableOrders.length <= 0 ) {
            await fetchAvailableOrders();
        }
    };

    const applyAddOrders = () => {
        const rows = isArray(selectedAddOrders) ? selectedAddOrders : [];

        const eligibleRows = rows.filter((row: any) => {
            const source = (get(row, 'source') || '').toString();
            const status = (get(row, 'load_status') || get(row, 'status') || '').toString().toLowerCase();

            // Existing outbound load rows are eligible only when announced.
            if ( source === 'alp_outbound_load_order' ) {
                return status === 'announced';
            }

            // Normal transport-request rows are eligible when stocked.
            return status === 'stocked';
        });

        if ( rows.length > 0 && eligibleRows.length <= 0 ) {
            toastify && toastify(t("only stocked orders or announced loads can be added to load"), "info");
            return;
        }

        if ( eligibleRows.length !== rows.length ) {
            toastify && toastify(t("some selected rows are not eligible and were skipped"), "info");
        }

        const merged = uniqBy([ ...(selectedNewOrders || []), ...eligibleRows ], (row: any) => String(get(row, 'id')));
        const ids = compact(merged.map((row: any) => String(get(row, 'id'))));

        setSelectedNewOrders(merged);
        if ( formikRef.current ) {
            formikRef.current.setFieldValue('new_orders', ids);
        }

        setAddOrdersModal(false);
    };

    const removeNewOrder = (id: any) => {
        const next = (selectedNewOrders || []).filter((row: any) => String(get(row, 'id')) !== String(id));
        const ids = compact(next.map((row: any) => String(get(row, 'id'))));
        setSelectedNewOrders(next);
        if ( formikRef.current ) {
            formikRef.current.setFieldValue('new_orders', ids);
        }
    };

    const markExistingOrderForDelete = (order: any) => {
        const orderId = String(get(order, 'id') || '');
        if ( isEmpty(orderId) ) {
            return;
        }

        const current = get(formikRef.current, 'values.deleted_orders', []);
        const next = uniq([ ...(isArray(current) ? current : []), orderId ]);

        if ( formikRef.current ) {
            formikRef.current.setFieldValue('deleted_orders', next);
        }
    };

    const undoExistingOrderDelete = (orderId: any) => {
        const current = get(formikRef.current, 'values.deleted_orders', []);
        const next = (isArray(current) ? current : []).filter((id: any) => String(id) !== String(orderId));
        if ( formikRef.current ) {
            formikRef.current.setFieldValue('deleted_orders', next);
        }
    };

    const orderErrorsById = useMemo(() => {
        const map: Record<string, string[]> = {};
        const rows = get(apiErrors, 'orders', []);
        if ( !isArray(rows) ) {
            return map;
        }

        forEach(rows, (row: any) => {
            const id = String(get(row, 'id') || '');
            if ( isEmpty(id) ) {
                return;
            }
            const errors = get(row, 'errors', get(row, 'error', []));
            if ( isArray(errors) ) {
                map[id] = errors.map((e: any) => String(e));
            } else if ( !isEmpty(errors) ) {
                map[id] = [ String(errors) ];
            } else {
                map[id] = [];
            }
        });

        return map;
    }, [apiErrors]);

    return (
        <>
            <div>
                <Sidebar
                    header={
                        <div className="bg-body-tertiary card-header">
                            <h5 className={ "card-title mt-0" }>{ t('edit load') }</h5>
                            <p className="mb-0">
                                <strong>{ t("group token") }:</strong> { props.id }
                            </p>
                        </div>
                    }
                    visible={ panelState }
                    position="right"
                    onHide={ onClose }
                    blockScroll={ true }
                    style={ { width: '900px' } }
                    closeOnEscape={ false }
                >
                    <div style={ {
                        height: '3px',
                        background: 'linear-gradient(90deg, var(--primary-color) 0%, rgba(33, 150, 243, 0) 50%)'
                    } }></div>

                    <Formik
                        innerRef={ formRef }
                        enableReinitialize={ true }
                        initialValues={ initFormState }
                        onSubmit={ onSubmit }
                        validationSchema={ validationSchema(t) }
                    >
                        { (fp: FormikProps<TInputForm>) => {
                            formikRef.current = fp;
                            return (
                            <Form onSubmit={ fp.handleSubmit }>
                                {
                                    !isEmpty(apiErrors) &&
                                    <div className="alert alert-danger mt-3 mb-0">
                                        <strong className="d-block mb-1">{ t('unprocessable entity') }</strong>
                                        <ul className="mb-0">
                                            {
                                                (() => {
                                                    const items: any[] = [];
                                                    forEach(apiErrors, (val: any, key: any) => {
                                                        if ( key === 'orders' ) {
                                                            return;
                                                        }

                                                        if ( isArray(val) ) {
                                                            forEach(val, (msg: any) => {
                                                                if ( !isEmpty(msg) ) {
                                                                    items.push(`${ key }: ${ String(msg) }`);
                                                                }
                                                            });
                                                            return;
                                                        }

                                                        if ( !isEmpty(val) ) {
                                                            items.push(`${ key }: ${ String(val) }`);
                                                        }
                                                    });

                                                    if ( items.length <= 0 && isArray(get(apiErrors, 'orders')) ) {
                                                        items.push(t('unprocessable entity'));
                                                    }

                                                    return items.map((msg: string, i: number) => (
                                                        <li key={ `${ msg }-${ i }` }>{ msg }</li>
                                                    ));
                                                })()
                                            }
                                        </ul>
                                    </div>
                                }
                                <Row className={ "mt-4 mb-7" }>
                                    <Col sm={ 12 } md={ 6 }>
                                        <Field
                                            component={ InputSingleSelectField }
                                            name="driver_id"
                                            trackBy="id"
                                            options={ driverOptions.filter((item: any) => item.is_active != false) }
                                            label={ t('driver') }
                                            placeholder={ t('select') + ' ' + t('driver') }
                                            isRequired
                                        />
                                    </Col>
                                    <Col sm={ 12 } md={ 6 }>
                                        <Field
                                            component={ InputSingleSelectField }
                                            panelClassName={ "hide-p-toggler" }
                                            name="transport_vehicle_id"
                                            trackBy="id"
                                            options={ transportVehicleOptions }
                                            label={ t('transport vehicle') }
                                            placeholder={ t('select') + ' ' + t('transport vehicle') }
                                            isRequired
                                        />
                                    </Col>
                                    <Col sm={ 12 } md={ 6 }>
                                        <Field
                                            placeholder={ t("pick a date") }
                                            component={ InputDateTime }
                                            name={ `estimated_loading_time` }
                                            type={ "datetime" }
                                            label={ t("estimated loading time") }
                                            isRequired
                                        />
                                    </Col>
                                    <Col sm={ 12 } md={ 6 }>
                                        <Field
                                            placeholder={ t("pick a date") }
                                            component={ InputDateTime }
                                            name={ `estimated_unloading_time` }
                                            type={ "datetime" }
                                            label={ t("estimated unloading time") }
                                            minDate={ moment().clone().subtract(30, 'days').toDate() }
                                            isRequired
                                        />
                                    </Col>
                                    <Col sm={ 12 } md={ 12 }>
                                        <Field
                                            component={ InputTextAreaField }
                                            name="comments"
                                            label={ t('comments') }
                                            placeholder={ t("enter") + ' ' + t("comments").toLowerCase() }
                                        />
                                    </Col>

                                    <Col sm={ 12 } md={ 12 } className={ "mb-5" }>
                                        <hr />
                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                            <h5 className="card-title mb-2">
                                                { t('orders') } <span className="text-muted">({ (loads || []).length })</span>
                                            </h5>
                                            {
                                                showAddOrders && canAddOrders &&
                                                <div className="d-flex align-items-center gap-2">
                                                    <Button
                                                        type="button"
                                                        size={ "small" }
                                                        outlined
                                                        onClick={ openAddOrders }
                                                    >
                                                        <i className="pi pi-plus me-2" />
                                                        { t('add') } { t('orders') }
                                                    </Button>
                                                </div>
                                            }
                                        </div>
                                        <GroupedLoadsByRoute
                                            loads={ loads }
                                            t={ t }
                                            showActionColumn={ true }
                                            isRowHidden={ (row: any) => {
                                                const order = get(row, 'alp_outbound_load_orders.0.order');
                                                const orderId = String(get(order, 'id') || '');
                                                const deletedOrders = get(fp, 'values.deleted_orders', []);
                                                return includes(isArray(deletedOrders) ? deletedOrders : [], orderId);
                                            } }
                                            getRowErrors={ (row: any) => {
                                                const order = get(row, 'alp_outbound_load_orders.0.order');
                                                const orderId = String(get(order, 'id') || '');
                                                return orderId ? (orderErrorsById[orderId] || []) : [];
                                            } }
                                            renderActionCell={ (row: any) => {
                                                const order = get(row, 'alp_outbound_load_orders.0.order');
                                                return (
                                                    <Tooltip tooltip={ t('delete item') }>
                                                        <a
                                                            title={ t('delete item') }
                                                            className={ "cursor-pointer ms-2 btn btn-soft-danger waves-effect waves-light btn-xs" }
                                                            onClick={ (event: any) => confirmPopup({
                                                                target: event.currentTarget,
                                                                message: t('do you want to delete this record?'),
                                                                icon: 'pi pi-info-circle',
                                                                // @ts-ignore
                                                                defaultFocus: 'reject',
                                                                acceptClassName: 'p-button-danger',
                                                                accept: () => markExistingOrderForDelete(order),
                                                                reject: () => {
                                                                },
                                                            }) }
                                                        >
                                                            <i className="fas fa-trash-alt" />
                                                        </a>
                                                    </Tooltip>
                                                );
                                            } }
                                        />

                                        {
                                            !isEmpty(get(fp, 'values.deleted_orders', [])) &&
                                            <div className="mt-3">
                                                <h5 className="card-title mb-2">
                                                    { t('deleted orders') } <span className="text-muted">({ (get(fp, 'values.deleted_orders', []) || []).length })</span>
                                                </h5>
                                                <div className="table-responsive">
                                                    <table className={ "fl-table w-full" }>
                                                        <thead>
                                                            <tr>
                                                                <th>#</th>
                                                                <th>{ t("vin number") }</th>
                                                                <th>{ t("status") }</th>
                                                                <th>{ t("brand") }</th>
                                                                <th>{ t("model") }</th>
                                                                <th>{ t("supplier compound") }</th>
                                                                <th>{ t("dealer") }</th>
                                                                <th className="text-end">{ t("action") }</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {
                                                                (get(fp, 'values.deleted_orders', []) || []).map((id: any, idx: number) => {
                                                                    const load = get(loadByOrderId, String(id));
                                                                    const order = get(load, 'alp_outbound_load_orders.0.order');

                                                                    const errors = String(id) ? (orderErrorsById[String(id)] || []) : [];

                                                                    return (
                                                                        <tr key={ String(id) || idx }>
                                                                            <td>{ idx + 1 }</td>
                                                                            <td className="text-uppercase">
                                                                                <VinWithType vinNumber={  get(order, 'vin_number') || "-" } />
                                                                                {
                                                                                    !isEmpty(errors) &&
                                                                                    <div className="text-danger small mt-1">
                                                                                        { (errors || []).join(', ') }
                                                                                    </div>
                                                                                }
                                                                            </td>
                                                                            <td className="text-uppercase">{ get(load, 'status') || "-" }</td>
                                                                            <td className="text-uppercase">{ get(order, 'brand.title') || "-" }</td>
                                                                            <td className="text-uppercase">{ get(order, 'brand_model.title') || "-" }</td>
                                                                            <td className="text-uppercase">{ get(load, 'supplier_compound') || "-" }</td>
                                                                            <td className="text-uppercase">{ get(load, 'dealer_name') || "-" }</td>
                                                                            <td className="text-end">
                                                                                <Tooltip tooltip={ t('undo') }>
                                                                                    <a
                                                                                        title={ t('undo') }
                                                                                        className={ "cursor-pointer ms-2 btn btn-soft-secondary waves-effect waves-light btn-xs" }
                                                                                        onClick={ () => undoExistingOrderDelete(id) }
                                                                                    >
                                                                                        <i className="fas fa-undo" />
                                                                                    </a>
                                                                                </Tooltip>
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })
                                                            }
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        }

                                        {
                                            !isEmpty(selectedNewOrders) &&
                                            <div className="mt-3">
                                                <h5 className="card-title mb-2">
                                                    { t('new orders') } <span className="text-muted">({ (selectedNewOrders || []).length })</span>
                                                </h5>
                                                <div className="table-responsive">
                                                    <table className={ "fl-table w-full" }>
                                                        <thead>
                                                            <tr>
                                                                <th>#</th>
                                                                <th>{ t("vin number") }</th>
                                                                <th>{ t("status") }</th>
                                                                <th>{ t("brand") }</th>
                                                                <th>{ t("model") }</th>
                                                                <th>{ t("location") }</th>
                                                                <th>{ t("supplier compound") }</th>
                                                                <th>{ t("dealer") }</th>
                                                                <th className="text-end">{ t("action") }</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            { (selectedNewOrders || []).map((row: any, idx: number) => (
                                                                <tr key={ String(get(row, 'id')) || idx }>
                                                                    <td>{ idx + 1 }</td>
                                                                    <td className="text-uppercase">
                                                                        <VinWithType vinNumber={  get(row, 'vin_number') || "-" } />
                                                                    </td>
                                                                    <td className="text-uppercase">{ get(row, 'load_status') || get(row, 'status') || "-" }</td>
                                                                    <td className="text-uppercase">{ get(row, 'brand.title') || "-" }</td>
                                                                    <td className="text-uppercase">{ get(row, 'brand_model.title') || "-" }</td>
                                                                    <td>
                                                                        <div>
                                                                            <Location item={ get(row, 'current_journey.from_location') } titled={ 1 } />
                                                                            <div><Location item={ get(row, 'current_journey.to_location') } titled={ 1 } /></div>
                                                                        </div>
                                                                    </td>
                                                                    <td className="text-uppercase">{ get(row, 'emil_frey_supplier_compound') || "-" }</td>
                                                                    <td className="text-uppercase">{ get(row, 'emil_frey_dealer') || "-" }</td>
                                                                    <td className="text-end">
                                                                        {
                                                                            !isEmpty(orderErrorsById[String(get(row, 'id'))]) &&
                                                                            <div className="text-danger small mb-1">
                                                                                { (orderErrorsById[String(get(row, 'id'))] || []).join(', ') }
                                                                            </div>
                                                                        }
                                                                        <Tooltip tooltip={ t('delete item') }>
                                                                            <a
                                                                                title={ t('delete item') }
                                                                                className={ "cursor-pointer ms-2 btn btn-soft-danger waves-effect waves-light btn-xs" }
                                                                                onClick={ (event: any) => confirmPopup({
                                                                                    target: event.currentTarget,
                                                                                    message: t('do you want to delete this record?'),
                                                                                    icon: 'pi pi-info-circle',
                                                                                    // @ts-ignore
                                                                                    defaultFocus: 'reject',
                                                                                    acceptClassName: 'p-button-danger',
                                                                                    accept: () => removeNewOrder(get(row, 'id')),
                                                                                    reject: () => {
                                                                                    },
                                                                                }) }
                                                                            >
                                                                                <i className="fas fa-trash-alt" />
                                                                            </a>
                                                                        </Tooltip>
                                                                    </td>
                                                                </tr>
                                                            )) }
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        }
                                    </Col>
                                </Row>

                                <div className="filter-card-footer" style={ { width: '885px' } }>
                                    <Button
                                        tooltip={ t("to data save into database") }
                                        size={ "small" }
                                        outlined
                                        tooltipOptions={ { position: 'top' } }
                                        disabled={ fp.isSubmitting }
                                    >
                                        <i className="pi pi-save me-2" />
                                        { t("let's save") }
                                    </Button>
                                    <Button
                                        type={ "button" }
                                        tooltip={ t("operation cancel") }
                                        className={ "ms-2" }
                                        size={ "small" }
                                        severity={ "danger" }
                                        outlined
                                        tooltipOptions={ { position: 'top' } }
                                        onClick={ onClose }
                                        disabled={ fp.isSubmitting }
                                    >
                                        { t("cancel") }
                                    </Button>
                                </div>
                            </Form>
                            );
                        } }
                    </Formik>

                    <Sidebar
                        header={
                            <div className="bg-body-tertiary card-header">
                                <h5 className={ "card-title mt-0 mb-1" }>{ t('add') } { t('orders') }</h5>
                                <div className="text-muted small">
                                    { t("group token") }: <strong>{ props.id }</strong>
                                </div>
                            </div>
                        }
                        visible={ addOrdersModal }
                        position="right"
                        onHide={ () => setAddOrdersModal(false) }
                        blockScroll={ true }
                        closeOnEscape={ false }
                        style={ { width: '1100px' } }
                    >
                        <div className="p-3">
                            { addOrdersLoading && <div className="text-muted mb-2">{ t('loading') }</div> }

                            <DataTable
                                value={ availableOrders }
                                dataKey="id"
                                selection={ selectedAddOrders }
                                onSelectionChange={ (e: any) => setSelectedAddOrders(e.value || []) }
                                scrollable
                                scrollHeight="620px"
                                stripedRows
                                showGridlines
                            >
                                <Column selectionMode="multiple" headerStyle={ { width: '3rem' } } />
                                <Column field="vin_number" header={ t('vin number') }
                                    body={(row: any) => <VinWithType vinNumber={ row.vin ?? row.vin_number } />}
                                />
                                <Column field="order_cd" header={ t('order') } />
                                <Column field="ldg_nr" header={ t('ldg nr') } />
                                <Column field="load_status" header={ t('status') } />
                                <Column field="emil_frey_supplier_compound" header={ t('supplier compound') } />
                                <Column field="emil_frey_dealer" header={ t('dealer') } />
                                <Column
                                    header={ t('from location') }
                                    body={ (row: any) => row?.current_journey?.from_location ? <Location item={ row.current_journey.from_location } titled={ 1 } /> : "-" }
                                />
                                <Column
                                    header={ t('to location') }
                                    body={ (row: any) => row?.current_journey?.to_location ? <Location item={ row.current_journey.to_location } titled={ 1 } /> : "-" }
                                />
                            </DataTable>
                        </div>

                        <div className="filter-card-footer" style={ { width: '1085px' } }>
                            <Button
                                type="button"
                                size={ "small" }
                                outlined
                                severity="secondary"
                                onClick={ () => setAddOrdersModal(false) }
                            >
                                { t('cancel') }
                            </Button>
                            <Button
                                type="button"
                                className={ "ms-2" }
                                size={ "small" }
                                severity="success"
                                onClick={ applyAddOrders }
                                disabled={ (selectedAddOrders || []).length <= 0 }
                            >
                                <i className="pi pi-plus me-2" />
                                { t('add') }
                            </Button>
                        </div>
                    </Sidebar>

                </Sidebar>
            </div>
            <ConfirmPopup />
        </>
    );
}

AlpOutboundLoadGroupUpdate.propTypes = {
    t: PropTypes.any,
};

export default withTranslation()(AlpOutboundLoadGroupUpdate);

